import Vue from "vue";
import { reflectKeys, jwt, appLocalStorage } from '@/app/shared/services';

import { fetchGuide, fetchTags, fetchArtefacts, fetchArtefact, fetchArtefactUnit } from '../services';

/** Initial state */
const initialState = {
  loading: false,
  data: null,
  error: null,
  guide: [],
  tags: [],
  artefacts: [],
  artefact: {},
  artefactUnit: {}
};

/** Prefix for mutation types and actiontypes */
const namespacedPrefix = '[SETUP]';

/**
 * Mutation types
 */
const mutationTypes = reflectKeys(
  [
    'SETUP_DATA_REQUEST',
    'SETUP_DATA_SUCCESS',
    'SETUP_DATA_ERROR',
    'SETUP_DATA_RESET',
    'LOOKUP_SETUP_GUIDE',
    'SET_SETUP_GUIDE',
    'SET_SETUP_GUIDE_ON',
    'LOOKUP_SETUP_TAGS',
    'SET_SETUP_TAGS',
    'LOOKUP_SETUP_ARTEFACTS',
    'SET_SETUP_ARTEFACTS',
    'LOOKUP_SETUP_ARTEFACT',
    'SET_SETUP_ARTEFACT',
    'LOOKUP_SETUP_ARTEFACT_UNIT',
    'SET_SETUP_ARTEFACT_UNIT',
    'RESET_SETUP'
  ],
  namespacedPrefix
);

const {
  SETUP_DATA_REQUEST,
  SETUP_DATA_SUCCESS,
  SETUP_DATA_ERROR,
  SETUP_DATA_RESET,
  LOOKUP_SETUP_GUIDE,
  SET_SETUP_GUIDE,
  SET_SETUP_GUIDE_ON,
  LOOKUP_SETUP_TAGS,
  SET_SETUP_TAGS,
  LOOKUP_SETUP_ARTEFACTS,
  SET_SETUP_ARTEFACTS,
  LOOKUP_SETUP_ARTEFACT,
  SET_SETUP_ARTEFACT,
  LOOKUP_SETUP_ARTEFACT_UNIT,
  SET_SETUP_ARTEFACT_UNIT,
  RESET_SETUP
} = mutationTypes;

/**
 * Auth data mutations
 */
const mutations = {
  /** auth data request */
  [SETUP_DATA_REQUEST](state) {
    Object.assign(state, { loading: true, error: null });
  },

  /** auth data success */
  [SETUP_DATA_SUCCESS](state, payload) {
    Object.assign(state, { loading: false, data: payload, user: payload, isAuthenticated: true });
  },

  /** auth data error */
  [SETUP_DATA_ERROR](state, payload) {
    Object.assign(state, {
      loading: false,
      data: null,
      error: payload || true
    });
  },

  /** reset auth data */
  [SETUP_DATA_RESET](state) {
    Object.assign(state, initialState);
  },

  [LOOKUP_SETUP_GUIDE](state) {
    state.loading = true;
  },
  [SET_SETUP_GUIDE](state, payload) {
      state.guide = payload.res.data;
  },
  [SET_SETUP_GUIDE_ON](state, guide) {
      state.guideOn = guide;
  },
  [LOOKUP_SETUP_TAGS](state) {
      state.loading = true;
  },
  [SET_SETUP_TAGS](state, payload) {
      state.tags = payload.res.data;
      state.tagsCount = payload.res.data ? payload.res.data.length : 0;
      state.loading = false;
  },
  [LOOKUP_SETUP_ARTEFACTS](state) {
      state.loading = true;
  },
  [SET_SETUP_ARTEFACTS](state, payload) {
      state.artefacts = payload.artefacts;
      state.artefactsCount = payload.artefacts ? payload.artefacts.length : 0;
      state.loading = false;
  },
  [LOOKUP_SETUP_ARTEFACT](state) {
      state.loading = true;
  },
  [SET_SETUP_ARTEFACT](state, artefact) {
      state.artefact = artefact;
  },
  [LOOKUP_SETUP_ARTEFACT_UNIT](state) {
      state.loading = true;
  },
  [SET_SETUP_ARTEFACT_UNIT](state, artefactUnit) {
      state.artefactUnit = artefactUnit;
  },
  [RESET_SETUP](state) {
      for (let f in state) {
          Vue.set(state, f, state.initialState[f]);
      }
  }

};

/** Actions types constants */
export const actionsTypes = reflectKeys([
  'FETCH_SETUP_GUIDE',
  'FETCH_SETUP_TAGS',
  'FETCH_SETUP_ARTEFACTS',
  'FETCH_SETUP_ARTEFACT',
  'FETCH_SETUP_ARTEFACT_UNIT',
  'RESET_SETUP_STATE'
], namespacedPrefix);

/**
 * Users data actions
 */
const actions = {

  async [actionsTypes.FETCH_SETUP_GUIDE](context, payload) {
    context.commit(LOOKUP_SETUP_GUIDE);
    return await fetchGuide(payload)
        .then(data => {
            payload.res = data;
            context.commit(SET_SETUP_GUIDE, payload);
        })
        .catch(error => {
            payload.res = error;
            context.commit(SET_SETUP_GUIDE, payload);
        });
  },
  async [actionsTypes.FETCH_SETUP_TAGS]({ commit }, payload) {
      commit(LOOKUP_SETUP_TAGS);
      return await fetchTags(payload)
          .then(data => {
              payload.res = data;
              commit(SET_SETUP_TAGS, payload);
          })
          .catch(error => {
              payload.res = error;
              commit(SET_SETUP_TAGS, payload);
          });
  },
  async [actionsTypes.FETCH_SETUP_ARTEFACTS]({ commit }, payload) {
      commit(LOOKUP_SETUP_ARTEFACTS);
      return await fetchArtefacts(payload)
          .then(data => {
              payload.res = data;
              commit(SET_SETUP_ARTEFACTS, payload);
          })
          .catch(error => {
              payload.res = error;
              commit(SET_SETUP_ARTEFACTS, payload);
          });
  },
  async [actionsTypes.FETCH_SETUP_ARTEFACT](context, payload, prevArtifact) {
      // avoid extronuous network call if search exists
      context.commit(LOOKUP_SETUP_ARTEFACT);
      if (prevArtifact !== undefined) {
          return context.commit(SET_SETUP_ARTEFACT, prevArtifact);
      }
      return await fetchArtefact(payload)
          .then(data => {
              payload.res = data.data.result;
              context.commit(SET_SETUP_ARTEFACT, payload);
          })
          .catch(error => {
              payload.res = {error: 'No records found for the search criteria.'};
              context.commit(SET_SETUP_ARTEFACT, payload);
              // throw new Error(error);
          });
  },
  async [actionsTypes.FETCH_SETUP_ARTEFACT_UNIT](context, payload, prevArtifact) {
      // avoid extronuous network call if search exists
      context.commit(LOOKUP_SETUP_ARTEFACT_UNIT);
      if (prevArtifact !== undefined) {
          return context.commit(SET_SETUP_ARTEFACT_UNIT, prevArtifact);
      }
      return await fetchArtefactUnit(payload)
          .then(data => {
              payload.res = data.data;
              context.commit(SET_SETUP_ARTEFACT_UNIT, payload);
          })
          .catch(error => {
            payload.res = {error: 'No records found for the given criteria.'};
              context.commit(SET_SETUP_ARTEFACT_UNIT, payload);
          });
  },
  [actionsTypes.RESET_SETUP_STATE]({ commit }) {
      commit(RESET_SETUP);
  } 
};

/**
 * Users data actions
 */
const state = {
  ...initialState,
  guideOn: [],
  loading: true,
  tagsCount: 0,
  artefactsCount: 0
};

const getters = {
  isLoading( state ) {
    return state.loading;
  },
  tagsCount( state ) {
      return state.tagsCount;
  },
  tags( state ) {
      return state.tags;
  },
  artefactsCount( state ) {
      return state.artefactsCount;
  },
  artefacts( state ) {
      return state.artefacts;
  },
  artefact( state ) {
      return state.artefact;
  },
  artefactUnit( state ) {
      return state.artefactUnit;
  },
  guide( state ) {
      return state.guide;
  },
  guideOn( state ) {
      return state.guideOn;
  }
};

export default {
  mutations,
  actions,
  state,
  getters
};
